(() => {
  if (window.__ottoBackInStockInit) return;
  window.__ottoBackInStockInit = true;

  const REVISION = '2026-07-15';
  const CLIENT_BASE = 'https://a.klaviyo.com/client';
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  const setError = (root, message, { html = false } = {}) => {
    const target = root.querySelector('[data-bis-error]');
    if (!target) return;
    if (!message) {
      target.hidden = true;
      target.textContent = '';
      return;
    }
    if (html) {
      target.innerHTML = message;
    } else {
      target.textContent = message;
    }
    target.hidden = false;
  };

  const eventProperties = (root) => ({
    product_id: root.dataset.productId || '',
    product_title: root.dataset.productTitle || '',
    product_handle: root.dataset.productHandle || '',
    product_type: root.dataset.productType || '',
    vendor: root.dataset.vendor || '',
    variant_id: root.dataset.variantId || '',
    variant_title: root.dataset.variantTitle || '',
    sku: root.dataset.sku || '',
    price: Number(root.dataset.price) || 0,
    price_formatted: root.dataset.priceFormatted || '',
    currency: root.dataset.currency || '',
    url: root.dataset.productUrl || '',
  });

  const postJson = async (url, body) => {
    const controller = new AbortController();
    let timeout;
    try {
      return await Promise.race([fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: REVISION },
      body: JSON.stringify(body),
      signal: controller.signal,
      }), new Promise((_, reject) => {
        timeout = setTimeout(() => { controller.abort(); reject(new Error('timeout')); }, 10000);
      })]);
    } finally { clearTimeout(timeout); }
  };

  const subscribe = (root, email) => {
    const companyId = root.dataset.companyId;
    const variantId = root.dataset.variantId;
    const properties = eventProperties(root);

    const subscription = postJson(
      `${CLIENT_BASE}/back-in-stock-subscriptions/?company_id=${encodeURIComponent(companyId)}`,
      {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: ['EMAIL'],
            profile: { data: { type: 'profile', attributes: { email } } },
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: `$shopify:::$default:::${variantId}`,
              },
            },
          },
        },
      }
    );

    // Mirrored as a metric so the product details are available to flows even
    // when the catalog variant is missing from the Klaviyo feed.
    const event = postJson(`${CLIENT_BASE}/events/?company_id=${encodeURIComponent(companyId)}`, {
      data: {
        type: 'event',
        attributes: {
          metric: {
            data: { type: 'metric', attributes: { name: 'Back in Stock Requested' } },
          },
          profile: { data: { type: 'profile', attributes: { email } } },
          properties,
        },
      },
    });

    return Promise.allSettled([subscription, event]);
  };

  const publish = (root, email) => {
    const payload = Object.assign({ email_hashed: email.length > 0 }, eventProperties(root));
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: 'back_in_stock_signup' }, payload));
    if (window.Shopify?.analytics?.publish) {
      window.Shopify.analytics.publish('back_in_stock_signup', payload);
    }
  };

  const submit = async (root) => {
    if (root.dataset.submitting === 'true') return;
    const form = root.querySelector('[data-bis-form]');
    const input = root.querySelector('[data-bis-email]');
    const consent = root.querySelector('[data-bis-consent]');
    const button = root.querySelector('[data-bis-submit]');
    const success = root.querySelector('[data-bis-success]');
    if (!input || !button) return;

    const email = (input.value || '').trim();

    if (!EMAIL_PATTERN.test(email)) {
      setError(root, root.dataset.errorEmail);
      input.setAttribute('aria-invalid', 'true');
      input.focus();
      return;
    }
    input.removeAttribute('aria-invalid');

    if (consent && !consent.checked) {
      setError(root, root.dataset.errorConsent);
      consent.focus();
      return;
    }

    setError(root, '');
    root.dataset.submitting = 'true';
    button.disabled = true;
    const label = button.querySelector('span') || button;
    label.textContent = root.dataset.labelSubmitting || label.textContent;

    const results = await subscribe(root, email);
    delete root.dataset.submitting;
    const accepted = results[0]?.status === 'fulfilled' && results[0].value.ok;
    const reachedKlaviyo = results.some((result) => result.status === 'fulfilled');

    if (accepted) {
      if (form) form.hidden = true;
      if (success) success.hidden = false;
      publish(root, email);
      success?.focus?.();
      return;
    }

    button.disabled = false;
    window.__ottoReportUIError?.('stock_signup');
    label.textContent = root.dataset.labelSubmit || label.textContent;

    if (reachedKlaviyo) {
      setError(root, root.dataset.errorGeneric);
    } else {
      setError(root, root.dataset.errorBlocked, { html: true });
    }
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('[data-bis-submit]');
    if (!button) return;
    const root = button.closest('[data-back-in-stock]');
    if (!root) return;
    event.preventDefault();
    submit(root);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter') return;
    const input = event.target.closest?.('[data-bis-email]');
    if (!input) return;
    const root = input.closest('[data-back-in-stock]');
    if (!root) return;
    // The field lives inside the product form; Enter must not add to cart.
    event.preventDefault();
    submit(root);
  });

  document.addEventListener('input', (event) => {
    const field = event.target.closest?.('[data-bis-email], [data-bis-consent]');
    if (!field) return;
    const root = field.closest('[data-back-in-stock]');
    if (root) setError(root, '');
  });
})();
